/**
 * Sound effects for the workout session.
 * One shared AudioContext, lazily resumed (browsers require a user gesture).
 */

let _ctx = null;

function getCtx() {
    if (!_ctx) {
        _ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (_ctx.state === "suspended") {
        _ctx.resume().catch(() => {});
    }
    return _ctx;
}

/** Call inside a user gesture (e.g., button click) to unlock the AudioContext
 *  so later sounds played from requestAnimationFrame are allowed. */
export async function primeAudio() {
    const ctx = getCtx();
    if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch (err) {
            console.warn("[sounds] AudioContext resume failed:", err);
        }
    }
}

/** Coin "ding" — fires on every rep. */
export function playBigCoin() {
    const ctx = getCtx();
    const now = ctx.currentTime;

    const voices = [
        { f1: 784, f2: 1175, type: "square",   gain: 0.15 }, // G5 → D6
        { f1: 392, f2: 587,  type: "triangle", gain: 0.10 }, // G4 → D5
    ];

    voices.forEach(({ f1, f2, type, gain: g }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(f1, now);
        osc.frequency.setValueAtTime(f2, now + 0.1);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(g,      now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.55);
    });
}

/** Ascending arpeggio + shimmer — fires on jackpot reps. */
export function playJackpot() {
    const ctx = getCtx();
    const baseStart = ctx.currentTime;

    const scale = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98, 2093.00];
    const noteDur = 0.08;
    const noteGap = 0.07;

    scale.forEach((freq, i) => {
        const start = baseStart + i * noteGap;

        const lead = ctx.createOscillator();
        const leadGain = ctx.createGain();
        lead.type = "square";
        lead.frequency.value = freq;
        leadGain.gain.setValueAtTime(0.0001, start);
        leadGain.gain.exponentialRampToValueAtTime(0.16, start + 0.005);
        leadGain.gain.exponentialRampToValueAtTime(0.0001, start + noteDur);
        lead.connect(leadGain).connect(ctx.destination);
        lead.start(start);
        lead.stop(start + noteDur + 0.02);

        const bass = ctx.createOscillator();
        const bassGain = ctx.createGain();
        bass.type = "triangle";
        bass.frequency.value = freq / 2;
        bassGain.gain.setValueAtTime(0.0001, start);
        bassGain.gain.exponentialRampToValueAtTime(0.1, start + 0.005);
        bassGain.gain.exponentialRampToValueAtTime(0.0001, start + noteDur);
        bass.connect(bassGain).connect(ctx.destination);
        bass.start(start);
        bass.stop(start + noteDur + 0.02);
    });

    const shimmerStart = baseStart + scale.length * noteGap;
    const shimmerDur = 0.4;
    [1567.98, 2093.00, 2637.02].forEach(freq => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.value = freq;

        lfo.frequency.value = 8;
        lfoGain.gain.value = 0.04;
        lfo.connect(lfoGain).connect(gain.gain);

        gain.gain.setValueAtTime(0.08, shimmerStart);
        gain.gain.exponentialRampToValueAtTime(0.0001, shimmerStart + shimmerDur);

        osc.connect(gain).connect(ctx.destination);
        osc.start(shimmerStart);
        osc.stop(shimmerStart + shimmerDur + 0.05);
        lfo.start(shimmerStart);
        lfo.stop(shimmerStart + shimmerDur + 0.05);
    });
}
